import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import api from '@/lib/api';
import { invoicesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, MagnifyingGlass, Receipt, Eye, ArrowCounterClockwise, Warning,
} from '@phosphor-icons/react';
import { SerialHistoryLink } from '@/components/SerialHistoryLink';

const fmt = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-';
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

export default function CreditNotesPage() {
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [aggregates, setAggregates] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Create CN state
  const [showCreate, setShowCreate] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [cnDate, setCnDate] = useState(new Date().toISOString().split('T')[0]);
  const [cnReason, setCnReason] = useState('');
  const [cnNotes, setCnNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // Detail view
  const [showDetail, setShowDetail] = useState(null);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/credit-notes', { params: { page, search } });
      setNotes(data.credit_notes || []);
      setTotal(data.total || 0);
      setAggregates(data.aggregates || {});
    } catch { toast.error('Failed to load credit notes'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  // Search invoices for CN creation
  const searchInvoices = async (q) => {
    setInvoiceSearch(q);
    if (q.length < 2) { setInvoices([]); return; }
    try {
      const { data } = await invoicesAPI.list({ search: q, status: 'all' });
      setInvoices((data.invoices || []).filter(inv => inv.payment_status !== 'credit_noted'));
    } catch { /* ignore */ }
  };

  const selectInvoice = async (inv) => {
    setSelectedInvoice(inv);
    setInvoices([]);
    setInvoiceSearch(inv.invoice_number);
    try {
      const { data } = await invoicesAPI.get(inv.id);
      setInvoiceDetail(data);
    } catch { toast.error('Failed to load invoice details'); }
  };

  const handleCreateCN = async () => {
    if (!selectedInvoice) { toast.error('Select an invoice first'); return; }
    setCreating(true);
    try {
      await api.post('/admin/credit-notes/create', {
        invoice_id: selectedInvoice.id,
        cn_date: cnDate,
        reason: cnReason,
        notes: cnNotes,
      });
      toast.success('Credit Note created! Inventory returned to stock.');
      setShowCreate(false);
      setSelectedInvoice(null);
      setInvoiceDetail(null);
      setInvoiceSearch('');
      setCnReason('');
      setCnNotes('');
      loadNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create credit note');
    } finally { setCreating(false); }
  };

  const perPage = 50;
  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="p-6" data-testid="credit-notes-page">
      <PageHeader title="Credit Notes" subtitle="Manage invoice reversals and returns" />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 mt-6 mb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
          <p className="text-xs text-[#64748B]">Total Credit Notes</p>
          <p className="text-2xl font-bold text-[#0F172A]">{aggregates.count || 0}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
          <p className="text-xs text-[#64748B]">Total Amount</p>
          <p className="text-2xl font-bold text-[#DC2626]">{fmt(aggregates.total_amount || 0)}</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search credit notes..." className="pl-9" data-testid="cn-search" />
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="create-cn-btn">
          <Plus size={16} className="mr-1.5" /> Create Credit Note
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="cn-table">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
              <th className="text-left p-3">CN #</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Invoice #</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Location</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-center p-3">Status</th>
              <th className="text-center p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : notes.length > 0 ? notes.map(cn => (
              <tr key={cn.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`cn-row-${cn.id}`}>
                <td className="p-3 font-medium text-[#0F4C81]">{cn.cn_number}</td>
                <td className="p-3 text-[#64748B]">{fmtDate(cn.cn_date)}</td>
                <td className="p-3 text-xs font-mono">{cn.invoice_number}</td>
                <td className="p-3">{cn.customer_name}</td>
                <td className="p-3 text-xs text-[#64748B]">{cn.clinic_name}</td>
                <td className="p-3 text-right font-medium text-[#DC2626]">{fmt(cn.grand_total)}</td>
                <td className="p-3 text-center">
                  <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">{cn.status}</span>
                </td>
                <td className="p-3 text-center">
                  <Button variant="ghost" size="sm" onClick={() => setShowDetail(cn)} data-testid={`cn-view-${cn.id}`}>
                    <Eye size={16} />
                  </Button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">No credit notes found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#64748B]">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Create CN Dialog */}
      <Dialog open={showCreate} onOpenChange={o => { if (!o) { setShowCreate(false); setSelectedInvoice(null); setInvoiceDetail(null); setInvoiceSearch(''); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-cn-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <ArrowCounterClockwise size={20} /> Create Credit Note
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Invoice Search */}
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Select Invoice *</label>
              <div className="relative">
                <Input value={invoiceSearch} onChange={e => searchInvoices(e.target.value)} placeholder="Search by invoice number or customer..." data-testid="cn-invoice-search" />
                {invoices.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {invoices.map(inv => (
                      <button key={inv.id} onClick={() => selectInvoice(inv)} className="w-full text-left px-3 py-2 hover:bg-[#F8FAFC] text-sm flex justify-between items-center">
                        <div>
                          <span className="font-medium">{inv.invoice_number}</span>
                          <span className="text-[#64748B] ml-2">{inv.customer_name || (inv.customer || {}).name}</span>
                        </div>
                        <span className="text-xs font-medium">{fmt(inv.grand_total || inv.total)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Invoice Preview */}
            {invoiceDetail && (
              <div className="border border-[#0F4C81] bg-[#EBF3FA] rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-[#0F4C81]">{invoiceDetail.invoice?.invoice_number}</h4>
                    <p className="text-xs text-[#64748B]">Date: {fmtDate(invoiceDetail.invoice?.date || invoiceDetail.invoice?.invoice_date)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#0F4C81]">{fmt(invoiceDetail.invoice?.grand_total || invoiceDetail.invoice?.total)}</p>
                    <p className="text-xs text-[#64748B]">Full Reversal</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-[#64748B]">Customer:</span> <strong>{invoiceDetail.customer?.contact_name || invoiceDetail.invoice?.customer_name}</strong></div>
                  <div><span className="text-[#64748B]">Location:</span> <strong>{invoiceDetail.invoice?.clinic_name}</strong></div>
                  {invoiceDetail.invoice?.lead_source && <div><span className="text-[#64748B]">Lead Source:</span> <strong>{invoiceDetail.invoice?.lead_source}</strong></div>}
                  {invoiceDetail.invoice?.salesperson_name && <div><span className="text-[#64748B]">Audiologist:</span> <strong>{invoiceDetail.invoice?.salesperson_name}</strong></div>}
                </div>

                {/* Line Items */}
                <table className="w-full text-xs mt-2">
                  <thead>
                    <tr className="border-b border-[#E2E8F0]">
                      <th className="text-left py-1">Item</th>
                      <th className="text-center py-1">Qty</th>
                      <th className="text-right py-1">Rate</th>
                      <th className="text-right py-1">Amount</th>
                      <th className="text-left py-1 pl-2">Serial #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoiceDetail.invoice?.line_items || []).map((li, i) => (
                      <tr key={i} className="border-b border-[#E2E8F0]">
                        <td className="py-1">{li.name}</td>
                        <td className="py-1 text-center">{li.quantity}</td>
                        <td className="py-1 text-right">{fmt(li.rate)}</td>
                        <td className="py-1 text-right">{fmt(li.amount || (li.quantity * li.rate))}</td>
                        <td className="py-1 pl-2">
                          {(li.serial_numbers || []).map(sn => (
                            <span key={sn} className="text-[10px] bg-[#E8F5E9] text-[#0F4C81] px-1 py-0.5 rounded font-mono mr-1">{sn}</span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="bg-[#FFF3E0] border border-orange-200 rounded-md p-2 flex items-center gap-2 text-xs text-orange-800">
                  <Warning size={16} />
                  <span>This will reverse the full invoice and return all serial numbers to available stock.</span>
                </div>
              </div>
            )}

            {/* CN Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Credit Note Date</label>
                <Input type="date" value={cnDate} onChange={e => setCnDate(e.target.value)} data-testid="cn-date" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Reason</label>
                <Input value={cnReason} onChange={e => setCnReason(e.target.value)} placeholder="Product return, defect..." data-testid="cn-reason" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Notes</label>
              <Input value={cnNotes} onChange={e => setCnNotes(e.target.value)} placeholder="Additional notes..." data-testid="cn-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreateCN} disabled={creating || !selectedInvoice} className="bg-[#DC2626] text-white hover:bg-[#B91C1C]" data-testid="cn-submit">
              {creating ? 'Creating...' : 'Create Credit Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CN Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={o => { if (!o) setShowDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cn-detail-dialog">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Plus_Jakarta_Sans']">{showDetail.cn_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#F8FAFC] rounded-lg p-3">
                    <p className="text-xs text-[#64748B]">Date</p>
                    <p className="text-sm font-medium">{fmtDate(showDetail.cn_date)}</p>
                  </div>
                  <div className="bg-[#F8FAFC] rounded-lg p-3">
                    <p className="text-xs text-[#64748B]">Amount</p>
                    <p className="text-sm font-bold text-[#DC2626]">{fmt(showDetail.grand_total)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-[#64748B]">Invoice:</span> <span className="font-mono">{showDetail.invoice_number}</span></div>
                  <div><span className="text-[#64748B]">Customer:</span> <strong>{showDetail.customer_name}</strong></div>
                  <div><span className="text-[#64748B]">Location:</span> {showDetail.clinic_name}</div>
                  {showDetail.reason && <div><span className="text-[#64748B]">Reason:</span> {showDetail.reason}</div>}
                  {showDetail.lead_source && <div><span className="text-[#64748B]">Lead Source:</span> {showDetail.lead_source}</div>}
                  {showDetail.audiologist_name && <div><span className="text-[#64748B]">Audiologist:</span> {showDetail.audiologist_name}</div>}
                </div>
                {showDetail.line_items?.length > 0 && (
                  <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-[#F8FAFC] text-xs text-[#64748B]">
                        <th className="text-left p-2">Item</th><th className="text-center p-2">Qty</th>
                        <th className="text-right p-2">Rate</th><th className="text-right p-2">Amount</th>
                        <th className="text-left p-2">Serial #</th>
                      </tr></thead>
                      <tbody>
                        {showDetail.line_items.map((li, i) => (
                          <tr key={i} className="border-t border-[#E2E8F0]">
                            <td className="p-2">{li.name}</td>
                            <td className="p-2 text-center">{li.quantity}</td>
                            <td className="p-2 text-right">{fmt(li.rate)}</td>
                            <td className="p-2 text-right font-medium">{fmt(li.amount || (li.quantity * li.rate))}</td>
                            <td className="p-2">{(li.serial_numbers || []).map(sn => <span key={sn} className="text-[10px] bg-[#E8F5E9] text-[#0F4C81] px-1 py-0.5 rounded font-mono mr-1">{sn}</span>)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
